// PO Spec Phase 4 §3 (2026-06-21) — Engagement aggregate dashboard
// tile. Mirrors `AmberAggregateTile` (which surfaces the §6.4
// Amber + Red distress signals) but for benign engagement signals:
// Support Space Activated + Resources Accessed.
//
// Privacy posture mirrors the Amber tile:
//   - Pool-at-floor: per-block count surfaces with the open-case
//     denominator ("3 of 7 open cases…") and the privacy threshold
//     echoed back.
//   - Pool-below-floor: a single "below the privacy floor of N" note;
//     no count is surfaced (a per-block count below the floor would
//     be a fingerprint, not an aggregate).
//   - Zero state: "no engagement in any open case" copy that mirrors
//     the Amber tile's "no amber/red signals in the last 90 days".
//
// The tile is a presentation layer over the structured server
// response — it MUST NOT compute its own thresholds or invent
// fields the API didn't send. The threshold is sourced from
// `block.threshold`, the count from `block.total`, and the
// denominator from `block.totalCases`.
import type { EngagementAggregate, EngagementAggregateBlock } from '@besideu/shared/hr-dashboard';

interface EngagementAggregateTileProps {
  data: EngagementAggregate;
}

export function EngagementAggregateTile({ data }: EngagementAggregateTileProps) {
  return (
    <section className="rounded-lg border border-border bg-background p-6">
      <header className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">Engagement signals</h2>
        <p className="text-xs text-muted-foreground">
          Counts only · open support pathways · k-anonymity floor applied
        </p>
      </header>
      <div className="grid gap-3 sm:grid-cols-2">
        <BlockSummary
          label="Support space activated"
          tone="ok"
          block={data.supportSpaceActivated}
        />
        <BlockSummary label="Resources accessed" tone="info" block={data.resourcesAccessed} />
      </div>
    </section>
  );
}

function BlockSummary({
  label,
  tone,
  block,
}: {
  label: string;
  tone: 'ok' | 'info';
  block: EngagementAggregateBlock;
}) {
  // Deliberately on the brand primary / neutral palette, NOT the traffic
  // tones — engagement is a positive/neutral signal, not distress, so it must
  // read differently from the Amber/Red wellbeing tiles. Tone is carried by
  // the border + fill; text stays `text-foreground` for WCAG AA (the teal
  // `text-primary` is only ~2.4:1 on a light fill — flagged in index.css).
  const toneClass =
    tone === 'ok'
      ? 'border-primary-soft-border bg-primary-soft text-foreground'
      : 'border-border bg-muted/40 text-foreground';
  return (
    <div className={`rounded-lg border p-4 ${toneClass}`}>
      <p className="text-3xl font-semibold tabular-nums" aria-label={`${label} total`}>
        {block.total}
      </p>
      <p className="mt-1 text-sm font-medium">{label}</p>
      <BlockBody block={block} />
    </div>
  );
}

function BlockBody({ block }: { block: EngagementAggregateBlock }) {
  if (block.suppressed) {
    if (block.totalCases === 0) {
      return <p className="mt-2 text-xs">No open support pathways in this organisation.</p>;
    }
    return (
      <p className="mt-2 text-xs">
        Pathway pool below the privacy floor of {block.threshold} (currently {block.totalCases}).
        Per-pathway counts are not surfaced for privacy.
      </p>
    );
  }
  return (
    <p className="mt-2 text-xs text-muted-foreground">
      {block.total} of {block.totalCases} open support{' '}
      {block.totalCases === 1 ? 'pathway' : 'pathways'}.
    </p>
  );
}
